// Financial-health indicators computed from the same posted journals + opening
// balances that drive the statements. Pure functions — no I/O.

import { profitAndLoss, balanceSheet, monthsCovered, journalsForMonth, accountMovements } from './computeFromJournals';
import { indexCOA } from './coa';

export function fmtMoney(n) {
  const v = Number(n) || 0;
  return (v < 0 ? '−' : '') + 'R ' + Math.abs(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtMoneyShort(n) {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  const s = v < 0 ? '−' : '';
  if (a >= 1e6) return s + 'R' + (a / 1e6).toFixed(1) + 'm';
  if (a >= 1e3) return s + 'R' + (a / 1e3).toFixed(1) + 'k';
  return s + 'R' + a.toFixed(0);
}

export function fmtPct(n) {
  if (n == null || !isFinite(n)) return '—';
  return (n * 100).toFixed(1) + '%';
}

export function fmtTimes(n) {
  if (n == null || !isFinite(n)) return '—';
  return n.toFixed(1) + '×';
}

function monthLabel(ym) {
  const parts = String(ym).split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mi = parseInt(parts[1], 10) - 1;
  return (names[mi] || parts[1]) + ' ' + (parts[0] || '').slice(2);
}

function bankCodes(coa) {
  return (coa || [])
    .filter((a) => a.type === 'asset' && (/^1000/.test(a.code) || /bank|cash|petty/i.test(a.name || '')))
    .map((a) => a.code);
}
function inventoryCodes(coa) {
  return (coa || [])
    .filter((a) => a.type === 'asset' && (/^1200/.test(a.code) || /inventory|stock/i.test(a.name || '')))
    .map((a) => a.code);
}

// Net balance of a set of accounts = (opening + movements) dr − cr.
function sumBalance(codes, opening, mv) {
  return codes.reduce((s, c) => {
    const o = (opening || {})[c] || { dr: 0, cr: 0 };
    const m = (mv || {})[c] || { dr: 0, cr: 0 };
    return s + ((o.dr || 0) + (m.dr || 0)) - ((o.cr || 0) + (m.cr || 0));
  }, 0);
}

// The position at the start of a period = opening balances + movements of every
// journal dated before it. Lets us scope balance-sheet ratios (ROE, inventory
// turnover, cash) correctly to a filtered date range.
export function openingAsAt(opening, journalsBefore) {
  const mv = accountMovements(journalsBefore || []);
  const out = {};
  const codes = new Set([...Object.keys(opening || {}), ...Object.keys(mv)]);
  codes.forEach((c) => {
    const o = (opening || {})[c] || { dr: 0, cr: 0 };
    const m = mv[c] || { dr: 0, cr: 0 };
    out[c] = { dr: (o.dr || 0) + (m.dr || 0), cr: (o.cr || 0) + (m.cr || 0) };
  });
  return out;
}

export function healthColour(ratio, kind) {
  if (ratio == null || !isFinite(ratio)) return '#9a9488';
  if (kind === 'expenseRatio') {
    if (ratio > 0.9) return '#b33';
    if (ratio > 0.75) return '#9a7d3d';
    return '#3d7a52';
  }
  if (ratio < 0) return '#b33';
  if (ratio < 0.05) return '#9a7d3d';
  return '#3d7a52';
}

export function computeInsights(coa, opening, journals) {
  const pl = profitAndLoss(coa, journals);
  const bs = balanceSheet(coa, opening, journals);
  const rev = pl.rev || 0;
  const banks = bankCodes(coa);
  const invCodes = inventoryCodes(coa);
  const allMv = accountMovements(journals);

  const openingEquity = balanceSheet(coa, opening, []).totalEquity;
  const closingEquity = bs.totalEquity;
  const openingInv = sumBalance(invCodes, opening, {});
  const closingInv = sumBalance(invCodes, opening, allMv);
  const avgEquity = (openingEquity + closingEquity) / 2;
  const avgInv = (openingInv + closingInv) / 2;

  const margins = {
    grossMargin: rev ? pl.gp / rev : null,
    operatingMargin: rev ? pl.opInc / rev : null,
    netMargin: rev ? pl.np / rev : null,
    expenseRatio: rev ? pl.opex / rev : null,
    roe: avgEquity > 0 ? pl.np / avgEquity : null,
    inventoryTurnover: avgInv > 0 ? pl.cogs / avgInv : null,
  };

  const months = monthsCovered(journals);
  const openCash = sumBalance(banks, opening, {});
  let cumProfit = 0;
  let acc = [];
  let prevEquity = openingEquity;
  let prevInv = openingInv;
  const monthly = months.map((ym) => {
    const mj = journalsForMonth(journals, ym);
    const mpl = profitAndLoss(coa, mj);
    cumProfit += mpl.np;
    acc = acc.concat(mj);
    const accMv = accountMovements(acc);
    const equityEnd = balanceSheet(coa, opening, acc).totalEquity;
    const invEnd = sumBalance(invCodes, opening, accMv);
    const cashEnd = sumBalance(banks, opening, accMv);
    const avgEq = (prevEquity + equityEnd) / 2;
    const avgIn = (prevInv + invEnd) / 2;
    const row = {
      ym, label: monthLabel(ym),
      revenue: mpl.rev, cogs: mpl.cogs, expenses: mpl.opex,
      netProfit: mpl.np, cumulativeProfit: cumProfit, cash: cashEnd,
      grossMargin: mpl.rev ? mpl.gp / mpl.rev : null,
      operatingMargin: mpl.rev ? mpl.opInc / mpl.rev : null,
      netMargin: mpl.rev ? mpl.np / mpl.rev : null,
      expenseRatio: mpl.rev ? mpl.opex / mpl.rev : null,
      roe: avgEq > 0 ? mpl.np / avgEq : null,
      inventoryTurnover: avgIn > 0 ? mpl.cogs / avgIn : null,
    };
    prevEquity = equityEnd;
    prevInv = invEnd;
    return row;
  });

  const byCode = indexCOA(coa);
  const expenses = [];
  Object.keys(allMv).forEach((code) => {
    const acc2 = byCode[code];
    if (!acc2) return;
    if (['expense', 'cogs', 'other_expense'].indexOf(acc2.type) < 0) return;
    const amt = (allMv[code].dr || 0) - (allMv[code].cr || 0);
    if (amt > 0) expenses.push({ code, name: acc2.name, amount: amt });
  });
  expenses.sort((a, b) => b.amount - a.amount);

  return {
    pl, bs, margins, monthly,
    topExpenses: expenses.slice(0, 6),
    hasInventory: invCodes.length > 0 && (Math.abs(openingInv) > 0.005 || Math.abs(closingInv) > 0.005),
    totals: {
      revenue: rev, cogs: pl.cogs, grossProfit: pl.gp, opex: pl.opex,
      operatingIncome: pl.opInc, otherIncome: pl.oi, otherExpense: pl.oe, netProfit: pl.np,
      totalAssets: bs.totalAssets, totalLiabilities: bs.totalLiabilities, totalEquity: bs.totalEquity,
      openingEquity, closingEquity, avgEquity, openingInv, closingInv, avgInv,
    },
  };
}

// Indicator catalogue for the trend chart (label, how to read a monthly value,
// formatter, colour). Used by the view's indicator selector.
export const INDICATORS = [
  { key: 'margins', label: 'Profit margins', unit: 'pct', kind: 'multi' },
  { key: 'netMargin', label: 'Net profit margin', unit: 'pct' },
  { key: 'operatingMargin', label: 'Operating margin', unit: 'pct' },
  { key: 'grossMargin', label: 'Gross margin', unit: 'pct' },
  { key: 'roe', label: 'Return on equity (ROE)', unit: 'pct' },
  { key: 'inventoryTurnover', label: 'Inventory turnover', unit: 'times' },
  { key: 'revenue', label: 'Revenue', unit: 'money' },
  { key: 'netProfit', label: 'Net profit', unit: 'money' },
  { key: 'cash', label: 'Cash balance', unit: 'money' },
];
